import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  AccessTime as ClockIcon,
  Business as BusinessIcon,
  LockOutlined as LockIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { login, getTenantBySlug, forgotPassword, verifyMfaLogin } from '../api';
import { setToken, setUser as setStoredUser } from '../utils/tenantStorage';

function Login({ onLogin, tenantSlug: urlTenantSlug, isAdminLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [currentTenant, setCurrentTenant] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [errorType, setErrorType] = useState(null);
  const [retrySeconds, setRetrySeconds] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingTenant, setLoadingTenant] = useState(true);
  const [tenantNotFound, setTenantNotFound] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotUsername, setForgotUsername] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState('');

  // MFA state
  const [mfaStep, setMfaStep] = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [backupCode, setBackupCode] = useState('');

  // Countdown timer for rate limiting
  useEffect(() => {
    if (retrySeconds > 0) {
      const timer = setTimeout(() => setRetrySeconds(retrySeconds - 1), 1000);
      return () => clearTimeout(timer);
    } else if (retrySeconds === 0 && (errorType === 'rate_limit' || errorType === 'lockout')) {
      setError('');
      setErrorType(null);
    }
  }, [retrySeconds, errorType]);

  // Save last tenant for returning users
  useEffect(() => {
    if (urlTenantSlug) {
      localStorage.setItem('lastTenantSlug', urlTenantSlug);
    }
  }, [urlTenantSlug]);

  useEffect(() => {
    const initialize = async () => {
      try {
        if (isAdminLogin) {
          // Admin login mode - no tenant needed
        } else if (urlTenantSlug) {
          try {
            const response = await getTenantBySlug(urlTenantSlug);
            setCurrentTenant(response.data);
          } catch (err) {
            if (err.response?.status === 404) {
              setTenantNotFound(true);
            } else {
              setError('Failed to load organization information');
            }
          }
        }
      } catch (err) {
        console.error('Failed to initialize login:', err);
        setError('Failed to load login page');
      } finally {
        setLoadingTenant(false);
      }
    };
    initialize();
  }, [urlTenantSlug, isAdminLogin]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (loading) return;

    setError('');
    setErrorType(null);
    setLoading(true);

    try {
      const loginData = { username, password };
      if (urlTenantSlug) {
        loginData.tenantSlug = urlTenantSlug;
      }

      const response = await login(loginData);

      // Check if MFA is required
      if (response.data.mfaRequired) {
        setMfaToken(response.data.mfaToken);
        setMfaStep(true);
        setLoading(false);
        return;
      }

      const { token, user, mfaSetupRequired } = response.data;

      const slug = user.tenantSlug || urlTenantSlug;
      setToken(token, slug);
      setStoredUser(user, slug);

      // If MFA setup is required, store flag so App.js can prompt setup
      if (mfaSetupRequired) {
        localStorage.setItem('mfaSetupRequired', 'true');
      }

      onLogin(user);
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Login failed. Please try again.';
      const retryAfter = err.response?.data?.retryAfter;

      if (err.response?.status === 423) {
        const remainingMinutes = err.response?.data?.remainingMinutes || 30;
        setErrorType('lockout');
        setRetrySeconds(remainingMinutes * 60);
        setError(errorMessage);
      } else if (err.response?.status === 429 || errorMessage.toLowerCase().includes('too many')) {
        setErrorType('rate_limit');
        setRetrySeconds(retryAfter ? retryAfter * 60 : 300);
        setError('Too many login attempts. Please wait before trying again.');
      } else if (errorMessage.toLowerCase().includes('invalid') || errorMessage.toLowerCase().includes('password')) {
        setErrorType('credentials');
        setError('Invalid username or password. Please check your credentials and try again.');
      } else {
        setErrorType('general');
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    setError('');
    setErrorType(null);
    setLoading(true);

    try {
      const response = await verifyMfaLogin(
        mfaToken,
        useBackupCode ? null : totpCode,
        useBackupCode ? backupCode : null
      );

      const { token, user } = response.data;
      const slug = user.tenantSlug || urlTenantSlug;
      setToken(token, slug);
      setStoredUser(user, slug);
      onLogin(user);
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Verification failed';
      if (errorMessage.toLowerCase().includes('expired')) {
        // MFA session expired — go back to password step
        setMfaStep(false);
        setMfaToken('');
        setTotpCode('');
        setBackupCode('');
        setError('Your session expired. Please sign in again.');
        setErrorType('general');
      } else {
        setError(errorMessage);
        setErrorType('credentials');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (forgotLoading || !forgotUsername.trim()) return;
    setForgotLoading(true);
    setForgotMessage('');
    try {
      await forgotPassword({ username: forgotUsername.trim(), tenantSlug: urlTenantSlug });
    } catch (err) {
      // Ignore errors - always show success
    }
    setForgotMessage('If an account with that username exists, a password reset email has been sent.');
    setForgotLoading(false);
  };

  const handleOidcLogin = () => {
    // Redirect to server-side OIDC authorize endpoint
    window.location.href = `/api/auth/oidc/${urlTenantSlug}/authorize`;
  };

  // Show error if tenant not found
  if (tenantNotFound) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #0D9488 0%, #14B8A6 100%)',
          padding: 2,
        }}
      >
        <Card sx={{ maxWidth: 400, width: '100%', boxShadow: 6 }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <BusinessIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Organization Not Found
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              The organization "{urlTenantSlug}" could not be found.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please check the URL or contact your administrator.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Show loading state
  if (loadingTenant) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #0D9488 0%, #14B8A6 100%)',
        }}
      >
        <CircularProgress sx={{ color: 'white' }} />
      </Box>
    );
  }

  const isOidc = currentTenant?.auth_method === 'oidc';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0D9488 0%, #14B8A6 100%)',
        padding: 2,
      }}
    >
      <Card sx={{ maxWidth: 400, width: '100%', boxShadow: 6 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <ClockIcon sx={{ fontSize: 60, color: 'primary.main', mb: 1 }} />
            <Typography variant="h4" component="h1" fontWeight="bold">
              HourTrax™
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Time Tracking Made Simple
            </Typography>
          </Box>

          {currentTenant && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.50', borderRadius: 2, textAlign: 'center' }}>
              <BusinessIcon sx={{ color: 'primary.main', mb: 0.5 }} />
              <Typography variant="h6" color="primary.main" fontWeight="medium">
                {currentTenant.display_name}
              </Typography>
            </Box>
          )}

          {isAdminLogin && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Program Administrator Login
            </Alert>
          )}

          {error && (
            <Alert
              severity={(errorType === 'rate_limit' || errorType === 'lockout') ? 'warning' : 'error'}
              sx={{ mb: 3 }}
              icon={(errorType === 'rate_limit' || errorType === 'lockout') ? <LockIcon /> : undefined}
            >
              {error}
              {(errorType === 'rate_limit' || errorType === 'lockout') && retrySeconds > 0 && (
                <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                  Try again in: {Math.floor(retrySeconds / 60)}:{(retrySeconds % 60).toString().padStart(2, '0')}
                </Typography>
              )}
            </Alert>
          )}

          {/* OIDC Login Button */}
          {isOidc && !mfaStep && !showForgotPassword && (
            <>
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleOidcLogin}
                sx={{
                  mb: 2,
                  py: 1.5,
                  bgcolor: '#0078d4',
                  '&:hover': { bgcolor: '#106ebe' },
                  textTransform: 'none',
                  fontSize: '1rem',
                }}
                startIcon={
                  <Box component="img" src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMSIgaGVpZ2h0PSIyMSI+PHJlY3Qgd2lkdGg9IjEwIiBoZWlnaHQ9IjEwIiBmaWxsPSIjZjI1MDIyIi8+PHJlY3QgeD0iMTEiIHdpZHRoPSIxMCIgaGVpZ2h0PSIxMCIgZmlsbD0iIzdmYmEwMCIvPjxyZWN0IHk9IjExIiB3aWR0aD0iMTAiIGhlaWdodD0iMTAiIGZpbGw9IiMwMGE0ZWYiLz48cmVjdCB4PSIxMSIgeT0iMTEiIHdpZHRoPSIxMCIgaGVpZ2h0PSIxMCIgZmlsbD0iI2ZmYjkwMCIvPjwvc3ZnPg==" alt="" sx={{ width: 20, height: 20 }} />
                }
              >
                Sign in with Microsoft
              </Button>
              <Divider sx={{ my: 2 }}>
                <Typography variant="body2" color="text.secondary">or sign in with password</Typography>
              </Divider>
            </>
          )}

          {/* MFA Verification Step */}
          {mfaStep ? (
            <>
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <SecurityIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h6">Two-Factor Authentication</Typography>
                <Typography variant="body2" color="text.secondary">
                  {useBackupCode
                    ? 'Enter one of your backup codes'
                    : 'Enter the 6-digit code from your authenticator app'}
                </Typography>
              </Box>

              <form onSubmit={handleMfaSubmit}>
                {!useBackupCode ? (
                  <TextField
                    fullWidth
                    label="Verification Code"
                    value={totpCode}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setTotpCode(val);
                    }}
                    margin="normal"
                    required
                    autoFocus
                    autoComplete="one-time-code"
                    inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
                    placeholder="000000"
                  />
                ) : (
                  <TextField
                    fullWidth
                    label="Backup Code"
                    value={backupCode}
                    onChange={(e) => setBackupCode(e.target.value.toUpperCase())}
                    margin="normal"
                    required
                    autoFocus
                    autoComplete="off"
                    placeholder="XXXX-XXXX"
                  />
                )}

                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading || (!useBackupCode && totpCode.length !== 6) || (useBackupCode && !backupCode.trim())}
                  sx={{ mt: 2, mb: 1, py: 1.5 }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Verify'}
                </Button>
              </form>

              <Box sx={{ textAlign: 'center', mt: 1 }}>
                <Typography
                  variant="body2"
                  color="primary"
                  sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                  onClick={() => {
                    setUseBackupCode(!useBackupCode);
                    setTotpCode('');
                    setBackupCode('');
                    setError('');
                  }}
                >
                  {useBackupCode ? 'Use authenticator app instead' : 'Use a backup code'}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ cursor: 'pointer', mt: 1, '&:hover': { textDecoration: 'underline' } }}
                  onClick={() => {
                    setMfaStep(false);
                    setMfaToken('');
                    setTotpCode('');
                    setBackupCode('');
                    setError('');
                  }}
                >
                  Back to login
                </Typography>
              </Box>
            </>
          ) : !showForgotPassword ? (
            <>
              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  margin="normal"
                  required
                  autoFocus={!isOidc}
                  autoComplete="username"
                />
                <TextField
                  fullWidth
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  margin="normal"
                  required
                  autoComplete="current-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading || ((errorType === 'rate_limit' || errorType === 'lockout') && retrySeconds > 0)}
                  sx={{ mt: 3, mb: 2, py: 1.5 }}
                >
                  {loading ? <CircularProgress size={24} /> :
                   ((errorType === 'rate_limit' || errorType === 'lockout') && retrySeconds > 0) ? 'Please Wait...' : 'Sign In'}
                </Button>
              </form>

              {urlTenantSlug && !isAdminLogin && (
                <Typography
                  variant="body2"
                  color="primary"
                  sx={{ textAlign: 'center', cursor: 'pointer', mb: 1, '&:hover': { textDecoration: 'underline' } }}
                  onClick={() => { setShowForgotPassword(true); setForgotMessage(''); setForgotUsername(username); }}
                >
                  Forgot Password?
                </Typography>
              )}

              <Typography variant="caption" color="text.secondary" display="block" textAlign="center">
                Contact your administrator if you need an account
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="h6" textAlign="center" sx={{ mb: 2 }}>
                Reset Password
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 2 }}>
                Enter your username and we'll send a password reset email if an account exists.
              </Typography>

              {forgotMessage && (
                <Alert severity="success" sx={{ mb: 2 }}>{forgotMessage}</Alert>
              )}

              <form onSubmit={handleForgotPassword}>
                <TextField
                  fullWidth
                  label="Username"
                  value={forgotUsername}
                  onChange={(e) => setForgotUsername(e.target.value)}
                  margin="normal"
                  required
                  autoFocus
                  autoComplete="username"
                />
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={forgotLoading || !forgotUsername.trim()}
                  sx={{ mt: 2, mb: 2, py: 1.5 }}
                >
                  {forgotLoading ? <CircularProgress size={24} /> : 'Send Reset Email'}
                </Button>
              </form>

              <Typography
                variant="body2"
                color="primary"
                sx={{ textAlign: 'center', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                onClick={() => { setShowForgotPassword(false); setForgotMessage(''); }}
              >
                Back to Login
              </Typography>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default Login;
